import { useCallback, useEffect, useMemo, useState } from 'react';
import paidAmountSettlementService from '../services/paidAmountSettlementService.js';
import {
  mlCreationHardBlocked,
  mlCreationHasOutstanding,
} from '../utils/mlPaidSettlementRules.js';
import { useRequirePaidSettlementBeforeMl } from './useRequirePaidSettlementBeforeMl.js';

const EMPTY_BLOCK = { blocked: false, has_outstanding: false };

export function useMlCreationPaidSettlement(user) {
  const role = user?.role;
  const isAgent = role === 'Agent';
  const isHubAdmin = role === 'Hub Admin';
  const applies = Boolean(isAgent || isHubAdmin);

  const [loaded, setLoaded] = useState(!applies);
  const [mlCreationBlock, setMlCreationBlock] = useState(EMPTY_BLOCK);
  const [dues, setDues] = useState(null);
  const [agentDues, setAgentDues] = useState([]);
  const [settleOpen, setSettleOpen] = useState(false);

  const requiresPaidBeforeMl = useRequirePaidSettlementBeforeMl(user);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!applies) {
      setMlCreationBlock(EMPTY_BLOCK);
      setDues(null);
      setAgentDues([]);
      setLoaded(true);
      return;
    }

    try {
      if (isHubAdmin) {
        const duesRes = await paidAmountSettlementService.getOpenDues();
        const items = Array.isArray(duesRes?.data?.items) ? duesRes.data.items : [];
        setAgentDues(items);

        const pending = items.find((row) => row.pending_request_id);
        const totalOutstanding = Number(duesRes?.data?.total_outstanding || 0)
          || items.reduce((sum, row) => sum + Number(row.amount_outstanding || 0), 0);

        if (pending) {
          setMlCreationBlock({
            blocked: true,
            has_outstanding: totalOutstanding > 0,
            reason: (
              `Agent ${pending.agent_name || pending.agent_user_id}: paid settlement `
              + `${pending.pending_request_id} is ${pending.pending_request_status || 'pending'}. `
              + 'Finish that approval before dispatching their Paid shipments on an ML sheet.'
            ),
            pending_request_id: pending.pending_request_id,
            pending_request_status: pending.pending_request_status,
            amount_outstanding: totalOutstanding,
            settlement_path: '/paid-amount-settlement-approvals',
          });
        } else if (requiresPaidBeforeMl && totalOutstanding > 0) {
          const names = items
            .filter((row) => Number(row.amount_outstanding || 0) > 0)
            .map((row) => row.agent_name || row.agent_user_id)
            .slice(0, 4)
            .join(', ');
          setMlCreationBlock({
            blocked: false,
            has_outstanding: true,
            reason: (
              `Hub agents have unremitted paid collections${names ? ` (${names})` : ''}. `
              + 'Those Paid shipments cannot be dispatched until settled — To Pay shipments are still allowed.'
            ),
            amount_outstanding: totalOutstanding,
            settlement_path: '/paid-amount-settlement-approvals',
          });
        } else {
          setMlCreationBlock(EMPTY_BLOCK);
        }
        setDues(null);
      } else {
        const blockRes = await paidAmountSettlementService.getMlCreationBlock();
        const block = blockRes?.data || EMPTY_BLOCK;
        setMlCreationBlock(block);
        setAgentDues([]);

        if (mlCreationHasOutstanding(block) || mlCreationHardBlocked(block)) {
          const duesRes = await paidAmountSettlementService.getOpenDues();
          setDues(duesRes?.data?.items?.[0] || null);
        } else {
          setDues(null);
        }
      }
    } catch {
      if (!silent) {
        setMlCreationBlock(EMPTY_BLOCK);
        setDues(null);
        setAgentDues([]);
      }
    } finally {
      setLoaded(true);
    }
  }, [applies, isAgent, isHubAdmin, requiresPaidBeforeMl]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ocrPending = mlCreationBlock.pending_request_status === 'OCR_PENDING';
  useEffect(() => {
    if (!ocrPending) return undefined;
    const timer = window.setInterval(() => refresh({ silent: true }), 5000);
    return () => window.clearInterval(timer);
  }, [ocrPending, refresh]);

  const settlementPath = useMemo(() => {
    if (mlCreationBlock.settlement_path) return mlCreationBlock.settlement_path;
    return isHubAdmin ? '/paid-amount-settlement-approvals' : '/paid-amount-settlements';
  }, [isHubAdmin, mlCreationBlock.settlement_path]);

  return {
    applies,
    isHubAdmin,
    loaded,
    mlCreationBlock,
    dues,
    agentDues,
    hardBlocked: mlCreationHardBlocked(mlCreationBlock),
    hasOutstanding: mlCreationHasOutstanding(mlCreationBlock),
    showGate: requiresPaidBeforeMl && (
      mlCreationHardBlocked(mlCreationBlock) || mlCreationHasOutstanding(mlCreationBlock)
    ),
    settlementPath,
    settleOpen,
    setSettleOpen,
    openSettle: () => {
      if (isHubAdmin) return false;
      if (dues?.pending_request_id) return false;
      setSettleOpen(true);
      return true;
    },
    refresh,
  };
}

export default useMlCreationPaidSettlement;
